import axios from "axios";
import { defineStore } from "pinia";
import { useConversation } from "./Conversation";
import { useWorkspace } from "./Workspaces";
import { useWorkspaceTree } from "./WorkspaceTree";
import { shouldSkipStateRestoration } from "../router";
import router from "../router";
import { RouteHelper } from "../Helpers/RouteHelper";
import { EntityResolver } from "../Services/EntityResolver";
import { EntityNavigator } from "../Services/EntityNavigator";
import {
    DEFAULT_MODULE,
    DEFAULT_PAGE,
    getModuleByName,
    MAIN_MODULES,
} from "../Constants/menus";
import Cache from "../Utilities/Cache";
import { useUserStore } from "./User";
import { useTimeTrackerStore } from "./TimeTracker";
import Utilities from "../Helpers/utilities";
import HttpStatusCodes from "../Constants/http";
import { useTeamPanel } from "./TeamPanel";
import { useActivity } from "./Activity";
import { nextTick } from "vue";
import { setColorsToStorage } from "../Helpers/colorPalette";
import { ROUTE_NAMES } from "../Constants/routes";
import { useUnlinkedPanelState } from "./useUnlinkedPanelState";

export const useAppState = defineStore("AppStateStore", {
    state: () => ({
        user_id: null,
        workspace_id: null,
        board_id: null,
        item_id: null,
        document_id: null,
        active_panel: "",
        // is_team_opened:false,
        conversation_id: null,
        is_conversation_open: null,

        spaces_panel_view: "kanban",
        boards_panel_view: "kanban",
        items_panel_view: "kanban",

        selected_variant_tab: "all",
        previous_selected_variant_tab: "all",
        locked_selected_variant_tab: null,
        tab: "",

        // is_document_kanban_view: null,
        is_document_open: false,

        updateStateThrottler: null,
        is_state_restoring_in_progress: 0,

        is_manage_organization_role_opened: false,
        role_object: {},
        current_division: null,

        invite_drawer: {
            visible: false,
            data: {},
            type: "item",
            new_group: false,
            is_team_opened: false,
            is_productivity_opened: false,
            is_workday_opened: false,
            is_workday_permission_opened: false,
        },

        productivity_drawer: {
            visible: false,
            data: {},
            type: "item",
            is_productivity_opened: false,
        },

        prospus_mouse_track_y: 0,
        prospus_mouse_track_x: 0,
        prospus_mouse_track: false,
        show_floating_buttons: true,
        user_is_typing_on_chat: false,
        page_loader: true,
        conv_loader: true,
        activity_details: false,
        selected_module: null,
        active_page: DEFAULT_PAGE,
        main_modules: MAIN_MODULES,
        activity_pinned_list: [],
        is_permission_error: false,
        usr_profile_logged_time: [],
        handle_fetch_logged_in_time_interval: null,
        is_in_active_window: null,
        is_internet_connected: true,
        team_filter: null,
        team: {
            selected_user_id: null,
            assigner_id: null,
            is_subordinate_panel: false,
            selected_subordinate_id: null,
        },
        countries: [],
        services: [],
        productive: {
            selected_user_id: null,
            selected_tab: null,
            productivity_opened: false,
            filter: {
                by_chronological: true,
                by_space: false,
                last_7_days: true,
                last_30_days: false,
                today: false,
                custom_dates: null,
            },
        },
        live_sync: {
            is_disconnected: false,
            connected_at: null,
            disconnected_at: null,
            connected_count: 0,
        },
        app_focus: {
            focus_in_at: null,
            focus_out_at: null,
        },
        saved_state: null,
        restoreSpaceStatePromise: null,
        show_control_panel: false,
        show_control_panel_active_index: 0,
        selection_histories: [],
        selection_history_index: 0,
        is_back_clicked: false,
        notification_confirm_dialog: true,
        skip_update_state: false,
        is_log_enable: false,
        in_between_restoring_status: null,
        has_opened_context_menu: false,
        message_input_height: null,
        is_agent: true,
        is_detail_mode: false,
        is_panel_loaded: false,
        is_dashboard_mode: false,
        tour_index: 0,
        contextMenuConvMap: new Map(),
        user_interaction: false,
        show_support_admin_dialog: false,
        esa_action_type: null,
        organization_timezones: [],
        ignore_cashed_module: false,
        invitation_accepted: null,
        restrictRestoreConversation: false,
        previous_route_name: null,
        skip_locate_animation: false,

        member_select: {
            active_index: null,
            selected_profile_id: null,
        },
        import_state: null,
    }),
    getters: {
        getModuleWork: (state) =>
            state.main_modules.find((module) => module.name === "work"),
        getModuleActivity: (state) =>
            state.main_modules.find((module) => module.name === "activity"),
        getModuleTeam: (state) =>
            state.main_modules.find((module) => module.name === "team"),
        is_group_locked_variant_tab: (state) =>
            state.locked_selected_variant_tab === "group",
        is_group_selected_variant_tab: (state) =>
            state.selected_variant_tab === "group",
        is_invite_selected_variant_tab: (state) =>
            state.selected_variant_tab === "invites",
        is_activity: (state) => state.active_page == "activity",
        is_team: (state) => state.active_page == "team",
        is_work: (state) => state.active_page == "work",
        is_align: (state) => state.active_page == "align",
        is_settings: (state) => state.selected_module?.name === "user-settings",
        is_directory: (state) => state.selected_module?.name === "directory",
        is_productive_panel_open: (state) =>
            state.productivity_drawer?.is_productivity_opened,
        selection_history: (state) => state.selection_histories,
        is_internet_disconnected: (state) => !state.is_internet_connected,
        is_logging_started: (state) => state.is_log_enable,
        is_user_interact_with_ui: (state) => state.user_interaction,
        show_enterprice_support_dialog: (state) =>
            state.show_support_admin_dialog,
        is_reports_active: (state) => state.selected_module?.name === "reports",
    },
    actions: {
        setImportState(state) {
            this.import_state = state;
        },
        openMemberSelect(index) {
            this.member_select.active_index = index;
            this.member_select.selected_profile_id = null;
        },

        confirmMemberSelect(profileId) {
            this.member_select.selected_profile_id = profileId;
        },

        resetMemberSelect() {
            this.member_select.active_index = null;
            this.member_select.selected_profile_id = null;
        },

        updateDocumentState(value = false) {
            this.is_document_open = value;
            this.updateState();
        },
        setTourIndex(n) {
            this.tour_index = n;
        },

        setRoleObject(roleObject = {}) {
            this.role_object = roleObject;
        },

        setCurrentDivision(division = null) {
            this.current_division = division;
        },

        disconnected() {
            this.live_sync.is_disconnected = true;
            this.live_sync.disconnected_at = Date.now();
        },
        connected() {
            this.live_sync.is_disconnected = false;
            this.live_sync.connected_at = Date.now();
            this.live_sync.connected_count = this.live_sync.connected_count + 1;
        },
        togglePageLoader(value = null) {
            if (value === null) {
                return (this.page_loader = !this.page_loader);
            }

            this.page_loader = value;
        },
        setSelectedVariantTab(value) {
            this.selected_variant_tab = value;
        },
        setPreviousSelectedVariantTab(value) {
            this.previous_selected_variant_tab = value;
        },
        resetSelectedVariantTab() {
            this.selected_variant_tab = "all";
        },
        setTab(value) {
            this.tab = value;
        },
        handleMembershipPanel() {
            if (this.previous_selected_variant_tab !== "members") {
                this.previous_selected_variant_tab = "all";
                this.selected_variant_tab = "all";
            } else if (this.invite_drawer.is_team_opened) {
                this.selected_variant_tab = "all";
            } else {
                this.selected_variant_tab = this.previous_selected_variant_tab;
            }
        },
        setLockedSelectedVariantTab(value) {
            this.locked_selected_variant_tab = value;
        },
        setActiveMouseTrack(value) {
            this.prospus_mouse_track = value;
        },
        setActiveFloatingBtns(value) {
            this.show_floating_buttons = value;
        },
        setActiveUserTyping(value) {
            this.user_is_typing_on_chat = value;
        },
        setMouseYpath(value) {
            this.prospus_mouse_track_y = value;
        },
        setInActiveWindow(value) {
            this.is_in_active_window = value;

            if (value) {
                this.app_focus.focus_out_at = Date.now();
            } else {
                this.app_focus.focus_in_at = Date.now();
            }

            // reload if app focus after 30 minutes
            if (
                this.app_focus.focus_out_at &&
                this.app_focus.focus_in_at &&
                this.app_focus.focus_in_at - this.app_focus.focus_out_at >
                    30 * 60 * 1000
            ) {
                // if focus in after 30 minutes app will reload.
                window.location.reload();
            }
        },
        async setInternetConnectivity(value) {
            try {
                await axios.get(`online-status`);
                this.is_internet_connected = true;
                useConversation().loadConversationKaamfuRequests();
            } catch (error) {
                const offline_error_codes = [
                    "ERR_NETWORK", // No network connection
                    "ECONNABORTED", // Connection timed out
                    "ERR_CONNECTION_REFUSED", // Connection refused,
                    "ENOTFOUND", // Domain not found
                ];

                if (offline_error_codes.includes(error.code)) {
                    this.is_internet_connected = false;
                    console.log("api", this.is_internet_connected);
                }
            }
        },
        setLoggingOn(value) {
            this.is_log_enable = value;
        },
        setUserInteraction(value) {
            this.user_interaction = value;
        },
        toggleNewGroupInvite(val) {
            this.invite_drawer.new_group = val ?? !this.invite_drawer.new_group;
        },

        toggleInviteDrawer(visible, objData = {}, is_team_opened) {
            const type = objData?.type ?? useWorkspace().invite_type;
            this.invite_drawer.visible = visible ?? !this.invite_drawer.visible;
            this.invite_drawer.data = objData;
            this.invite_drawer.type = type;
            this.invite_drawer.is_team_opened = is_team_opened ?? false;
        },

        toggleProductivityDrawer(val, is_update_state = true) {
            this.productivity_drawer.is_productivity_opened = val ?? false;

            if (!is_update_state) return;

            const productive = this.productive;
            if (productive)
                productive.productivity_opened =
                    this.productivity_drawer.is_productivity_opened;

            this.updateProductiveState(productive);
        },

        setControlAsSelectedModule(val) {
            this.show_control_panel = val;
        },
        setStoreAsSelectedModule() {
            this.selected_module = this.main_modules.find(
                (module) => module.name === "store",
            );
        },
        setWorkAsSelectedModule() {
            const module = this.main_modules.find(
                (module) => module.name === "work",
            );
            this.setSelectedModule(module);
        },
        setActivityAsSelectedModule() {
            const module = this.main_modules.find(
                (module) => module.name === "activity",
            );
            this.setSelectedModule(module);
        },
        setTeamAsSelectedModule() {
            const module = this.main_modules.find(
                (module) => module.name === "team",
            );
            this.setSelectedModule(module);
        },
        setGoalsAsSelectedModule() {
            this.selected_module = this.main_modules.find(
                (module) => module.name === "goals",
            );
        },
        setPlannerAsSelectedModule() {
            this.selected_module = this.main_modules.find(
                (module) => module.name === "planner",
            );
        },

        updateSelectedModule(name) {
            this.selected_module = getModuleByName(name);
            this.active_page = name;
        },

        setSelectedModule(data, is_restore = false) {
            if (!data) return;

            // if (!useUnlinkedPanelState().isUnlinkedPanelActive) {
            //     Utilities().toggleChatPopover(false);
            // }

            this.updateSelectedModule(data.name);

            const cache = new Cache();
            cache.set("active_page", this.active_page);

            setTimeout(() => {
                this.restoreActivityState();
                if (is_restore) {
                    useWorkspace().restoreEditorState(
                        this.document_id,
                        this.item_id,
                    );
                }
            }, 100);

            if (!is_restore) {
                useWorkspace().toggleDocument();
                this.is_document_open = false;
            }

            this.updateState();
        },
        setDrawerType(type) {
            this.invite_drawer.type = type;
        },
        openDocumentInActivity() {
            this.is_document_open = true;
            this.updateState();
        },
        closeDocumentInActivity() {
            this.is_document_open = false;
            this.updateState();
        },
        updateDocumentStateInActivity(status) {
            this.is_document_open = status;
            this.updateState();
        },
        toggleActivityDetails(is_visible = true) {
            this.activity_details = is_visible;
            this.updateState();
        },
        initializeWith(data) {
            this.startRestoringState();
            if (!this.ignore_cashed_module) {
                //this.selected_module = DEFAULT_MODULE;
                this.ignore_cashed_module = false;
            }

            this.$patch(data);
            //console.log("after setting", this.workspace_id);

            // Skip state restoration for direct navigation routes
            if (!shouldSkipStateRestoration()) {
                this.restoreState();
            } else {
                // this is make sure all other state like conv history and task history loaded
                this.restoreSpaceState(null, 0);
            }
        },

        startRestoringState() {
            this.is_state_restoring_in_progress += 1;
        },
        stopRestoringState() {
            this.is_state_restoring_in_progress -= 1;

            /* if (this.in_between_restoring_status) {
                this.save(this.in_between_restoring_status);
            }*/
        },
        setPinnedItems(value) {
            this.activity_pinned_list = value;
        },
        async restoreState() {
            await this.restoreSpaceState();
            this.restoreConversationState();

            timeOutHelper(() => {
                this.restoreActivityState();
            }, 100);
        },
        restoreRoute(state) {
            const module = getModuleByName(state?.active_page);
            if (!module) return;

            this.setSelectedModule(module);

            // Only restore route if we have a selected module and it's not the default work module
            //if (this.selected_module && this.selected_module.name !== ROUTE_NAMES.WORK) {
            const routeName = this.selected_module.name;

            // Import router dynamically to avoid circular dependency
            import("../router").then(({ default: router }) => {
                // Only navigate if we're not already on the target route
                if (router.currentRoute.value.name !== routeName) {
                    router.push({ name: routeName });
                }
            });
            //}
        },
        goToHome() {
            this.goToRoute(ROUTE_NAMES.HOME);
        },
        goToWork() {
            this.goToRoute(ROUTE_NAMES.WORK);
        },
        goToActivity() {
            this.goToRoute(ROUTE_NAMES.ACTIVITY);
        },
        goToDirectory() {
            this.goToRoute(ROUTE_NAMES.DIRECTORY);
        },
        goToSettings() {
            this.goToRoute(ROUTE_NAMES.SETTINGS);
        },
        goToTeam() {
            this.goToRoute(ROUTE_NAMES.TEAM);
        },
        goToReports() {
            this.goToRoute(ROUTE_NAMES.REPORTS);
        },
        goToAlign() {
            this.goToRoute(ROUTE_NAMES.ALIGN);
        },
        goToRoute(routeName) {
            // Import router dynamically to avoid circular dependency
            import("../router").then(({ default: router }) => {
                if (router.currentRoute.value.name !== routeName)
                    router.push({ name: routeName });
            });
        },
        setPinnedList(activity_pinned_items) {
            const activity_pinned_list = activity_pinned_items.map((str) => {
                return Number(str);
            });

            this.setPinnedItems([...new Set(activity_pinned_list)]);
        },
        setPanelState(state) {
            const active_page = state.active_page;

            if (active_page)
                this.setSelectedModule(getModuleByName(active_page), true);
            else this.setSelectedModule(DEFAULT_MODULE, true);
        },
        async restoreSpaceState(state = null, is_state_to_restore = 1) {
            // Prevent multiple simultaneous restoration calls
            if (this.restoreSpaceStatePromise) {
                return this.restoreSpaceStatePromise;
            }

            /* const is_activity = DEFAULT_MODULE.name == "activity";
            if (is_state_to_restore && !this.workspace_id && !is_activity) {
                this.stopRestoringState();
                return;
            } */
            // Set promise IMMEDIATELY to prevent race conditions
            this.restoreSpaceStatePromise = (async () => {
                //load workspace boards
                const workspace_store = useWorkspace();
                const conversation_store = useConversation();
                try {
                    if (state === null) {
                        const { data } = await axios.get(
                            `/user-states?load-space-board=1`,
                        );
                        state = data;
                    }

                    this.restoreProductivePanel(state.productive);
                    this.setPinnedList(state.activity_pinned_list);
                    this.restoreSelectionHistory(state?.histories);
                    conversation_store.restoreRecentlyOpenedConversations(
                        state?.recently_opened_conversations,
                    );

                    if (is_state_to_restore) {
                        this.updateSelectedItemId(state.item_id);
                        this.updateEditor(state.document_id);
                        this.updateConversationId(state.conversation_id);
                        this.updateConversationState(
                            state.is_conversation_open,
                        );
                        Utilities().toggleChatPopoverWithoutUpdatingState(
                            state.is_conversation_open,
                        );

                        this.updateDocumentStateInActivity(
                            state.is_document_open,
                        );
                        workspace_store.loadLastOpenedSpaceAndBoard(state);
                        workspace_store.restoreActivePanel(this.active_panel);
                        workspace_store.restoreEditorState(
                            this.document_id,
                            this.item_id,
                        );

                        const active_item = useWorkspaceTree()?.activeItem;

                        if (state?.is_link_data && active_item) {
                            workspace_store.setLocateClassName("item");
                        }

                        if (!this.selected_module) this.setPanelState(state);

                        if (this.is_team) {
                            this.closeDocumentInActivity(false);
                            useWorkspace().setEditorView(false);
                            useWorkspace().toggleDocument();
                        }

                        this.restoreDocumentState(state);
                    }
                } catch (error) {
                    if (error.response?.status === HttpStatusCodes.FORBIDDEN)
                        this.setPermissionError(true);
                } finally {
                    this.stopRestoringState();
                }
            })();

            return this.restoreSpaceStatePromise;
        },
        restoreConversationState() {
            if (this.restrictRestoreConversation) {
                return this.enableRestoreConversation();
            }
            this.startRestoringState();
            const conversation_store = useConversation();

            if (!this.productive?.productivity_opened)
                conversation_store.restore(
                    this.conversation_id,
                    this.is_conversation_open,
                );

            this.stopRestoringState();
        },
        restoreDocumentState(state) {
            if (this.is_activity && state.is_document_open) {
                useActivity().toggleDocument(true, state.conversation_id);
            } else if (state.document_id) {
                if (this.is_team) {
                    useTeamPanel().toggleDocument(
                        state.is_document_open,
                        state.conversation_id,
                    );
                } else if (this.is_work) {
                    nextTick(() => {
                        useWorkspace().toggleDocument(
                            true,
                            state.conversation_id,
                        );
                    });
                }
            }
        },
        updateState() {
            const state = this.getCurrentUserState();
            if (this.is_state_restoring_in_progress) {
                this.in_between_restoring_status = state;
                return;
            }

            this.save(state);
        },
        save(state) {
            if (this.is_state_restoring_in_progress) {
                this.in_between_restoring_status = state;
                return;
            }
            if (this.is_detail_mode) {
                state.item_view_mode = "detail";
            }
            if (
                (this.saved_state &&
                    JSON.stringify(this.saved_state) ===
                        JSON.stringify(state)) ||
                this.skip_update_state
            ) {
                // data is same. therefore, api call is not required.
                return;
            }

            axios.patch("/user-states", state);
            this.saved_state = state;
        },
        getCurrentUserState() {
            return {
                workspace_id: this.workspace_id,
                board_id: this.board_id,
                item_id: this.item_id,
                document_id: this.document_id,
                conversation_id: this.conversation_id,
                is_conversation_open: this.is_conversation_open,
                // is_document_kanban_view: this.is_document_kanban_view,
                active_panel: this.active_panel,
                active_page: this.active_page,
                is_document_open: this.is_document_open,
                team_filter: this.team_filter,
                team: this.team,
                productive: this.productive,
                tour_index: this.tour_index,
            };
        },
        updateWorkspaceId(id) {
            this.workspace_id = id;
            this.board_id = null;
            this.updateState();
        },
        updateBoardId(id) {
            this.board_id = id;
            this.updateState();
        },
        updateSelectedItemId(item_id) {
            this.item_id = item_id;
            this.addItemInSelectionHistory();
            this.updateState();
        },
        updateConversationId(id) {
            if (!id)
                return console.log(`Invalid converation for state restroing`);
            this.conversation_id = id;
            this.updateState();
        },
        updateConversationState(is_opened) {
            this.is_conversation_open = is_opened;
            this.updateState();
        },
        restoreSelectionHistory(histories) {
            this.selection_histories = histories;
        },
        updateEditor(id) {
            if (this.is_state_restoring_in_progress) return;

            this.document_id = id;
            this.updateState();
        },
        updateActivePanel(panel_view) {
            if (this.is_state_restoring_in_progress) return;

            this.active_panel = panel_view;
            this.updateState();
        },
        updateTeamFilter(filter) {
            this.team_filter = filter;
            this.updateState();
        },
        updateProductiveState(productive_state) {
            this.productive = productive_state;
            this.updateState();
        },

        updateTeam(value) {
            if (
                value["selected_user_id"] === this.team["selected_user_id"] &&
                value["is_subordinate_panel"] ===
                    this.team["is_subordinate_panel"] &&
                value["selected_subordinate_id"] ===
                    this.team["selected_subordinate_id"] &&
                value["assigner_id"] === this.team["assigner_id"]
            )
                return;

            this.team = value;
            this.updateState();
        },
        async restoreActivityState() {
            if (!this.is_activity) return;

            const active_conv = useConversation().active;

            if (!active_conv?.id || !this.is_conversation_open) return;

            useConversation().setDocumentByConversationId(active_conv?.id);

            if (!this.is_document_open) {
                return this.toggleActivityDetails(false);
            }

            useWorkspace().setActiveChatData(active_conv);
            this.toggleActivityDetails(true);
        },
        updateSelectionHistoryByItem(item) {
            this.selection_histories = this.selection_histories.map((data) => {
                if (data.type === item.type && item.id === data.id) {
                    data.name = item.name;
                }
                return data;
            });
        },
        setBrowserHistory() {
            const url = new URL(window.location.href);
            if (!url.pathname.replace(/^\/+|\/+$/g, "")) return;

            const new_url = new URL(url.protocol + "//" + url.hostname);
            history.replaceState({}, "", new_url.toString());
        },
        setPermissionError(value) {
            this.is_permission_error = value;
        },
        async fetchLoggedTime() {
            // it can be improved in future and only call if loggedin user has subordinates
            const { data } = await axios.get("/shift-timer/shift");
            this.usr_profile_logged_time = data.data;

            this.startShiftFetchingTimer();
        },
        swapShiftToPause(user_profile_id) {
            const founded = this.usr_profile_logged_time.find(
                (shift) => shift.user_profile_id == user_profile_id,
            );

            if (founded) {
                founded.paused_task = founded.active_task;
                founded.active_task = null;
                founded.event_type = useTimeTrackerStore().Shift_Pause;
            }
        },
        startShiftFetchingTimer() {
            if (this.handle_fetch_logged_in_time_interval) return;

            this.handle_fetch_logged_in_time_interval = setInterval(() => {
                axios.get("/shift-timer/shift").then(({ data }) => {
                    this.usr_profile_logged_time = data.data;
                });
            }, 60000);
        },
        stopShiftFetchingTimer() {
            clearInterval(this.handle_fetch_logged_in_time_interval);
        },
        getShiftInUsers() {
            const task_start = useTimeTrackerStore().Task_Start;
            const task_stop = useTimeTrackerStore().Task_Stop;
            const task_pause = useTimeTrackerStore().Task_Pause;
            let profiles = [];
            this.usr_profile_logged_time.forEach((shift_in_user) => {
                const profile = useUserStore().getUserProfile(
                    shift_in_user.user_profile_id,
                );

                if (profile) {
                    //   profile.timer = convertMinutesToHoursAndMinutes(shift_in_user.total_minutes);
                    //   profile.progress = calculateDayProgress(shift_in_user.total_minutes);

                    /*
                         no badge = no task selected.
                         green = task selected,
                         gray = task paused / shift paused
                         red = blocked, waiting for something. ( implementation not done yet)

                         */
                    const user_profile_id =
                        shift_in_user?.active_task?.version
                            ?.user_profile_id_creator ||
                        shift_in_user?.paused_task?.version
                            ?.user_profile_id_creator;

                    profile.active_task = shift_in_user.active_task;
                    profile.paused_task = shift_in_user.paused_task;
                    profile.task_status = task_stop;
                    profile.shift_status = shift_in_user?.event_type;
                    profile.is_team = shift_in_user.is_team;

                    profile.assigner_info = user_profile_id
                        ? useUserStore().getUserProfile(user_profile_id)
                        : user_profile_id;
                    if (
                        this.hasActiveTask(profile) &&
                        useTimeTrackerStore().isShiftRunning(
                            shift_in_user?.event_type,
                        )
                    )
                        profile.task_status = task_start;
                    else if (
                        useTimeTrackerStore().isShiftPauseOrUnreachableOrInActive(
                            shift_in_user?.event_type,
                        )
                    )
                        profile.task_status = task_pause;

                    profiles.push(profile);
                }
            });
            return profiles;
        },
        getShiftInUsersForTeamStatus() {
            return this.getShiftInUsers().filter(
                (profile) => !useUserStore().isCurrentProfile(profile.id),
            );
        },

        getShiftInUsersForTeamStatusByUserId(user_id) {
            return this.getShiftInUsers().filter(
                (profile) => profile.id != user_id,
            );
        },

        hasActiveTask(profile) {
            return profile?.active_task;
        },
        restoreProductivePanel(productive, force_open = false) {
            this.productive = productive;
            const is_open = productive?.productivity_opened || force_open;
            this.toggleProductivityDrawer(is_open, false);
            if (is_open) Utilities()?.toggleChatPopover(is_open);
        },
        async handleSharedLink(url, failCallbck = null) {
            const token = new URL(url).pathname.replace(/^\/+|\/+$/g, "");
            if (!token) return;

            try {
                // Handle /work/HANDLE-SEQUENCE format
                if (token.startsWith("work/")) {
                    const entityId = token.substring(5);
                    if (EntityResolver.isValidFormat(entityId)) {
                        router.push(`/${token}`);
                        return;
                    }
                }

                // Handle hash-based links
                await this.handleHashBasedLink(token);
            } catch (e) {
                console.error("Failed to handle shared link:", e);
                failCallbck?.();
            }
        },
        async handleHashBasedLink(token) {
            try {
                const { data } = await axios.get(`/links/${token}`);
                const item = data.data.item;

                if (item?.sequence_number) {
                    const url = RouteHelper.getEntityUrl(item);

                    if (router.currentRoute.value.path === url) {
                        await this.handleEntityLinkAlreadyActive(item);
                        return;
                    }

                    RouteHelper.navigateToEntity(item);
                } else {
                    throw new Error("Not a valid link");
                }
            } catch (e) {
                console.error("Failed to handle  link:", e);
                throw e;
            }
        },

        async handleEntityLinkAlreadyActive(item) {
            const handle = useUserStore().profile?.organization?.handle;

            if (!handle) {
                console.warn("RouteHelper: Unable to get organization handle");
                return null;
            }

            const entity_id = `${handle}-${item.sequence_number}`;

            return await this.handleEntityLinkAlreadyActiveByUniversalId(
                entity_id,
            );
        },

        async handleEntityLinkAlreadyActiveByUniversalId(universal_id) {
            const entityData = await EntityResolver.resolveEntity(universal_id);

            // Navigate to entity (loads data + triggers LocationAnimation)
            await EntityNavigator.navigateToEntity(entityData);
        },

        addItemInSelectionHistory() {
            if (!this.selection_histories) this.selection_histories = [];

            const tree = useWorkspaceTree();
            const active_item = tree.activeItem;
            if (
                !active_item ||
                !active_item?.id ||
                this.is_state_restoring_in_progress
            ) {
                return;
            }

            const index = this.selection_histories.findIndex(
                (history) => history.id == active_item.id,
            );

            if (index === 0) {
                return;
            }

            if (index > -1) {
                this.selection_histories.splice(index, 1);
            } else if (!this.is_back_clicked) {
                this.selection_history_index = 0;
            }

            console.log("added to history", active_item.name);

            const treeItem = tree.getItemById(active_item.id);
            const workspaceForItem = treeItem
                ? tree.getWorkspaceById(
                      treeItem.workspaceId ?? treeItem.workspace_id,
                  )
                : null;
            const workspace_avatar = workspaceForItem?.avatar_url;

            this.selection_histories.splice(0, 0, {
                id: active_item.id,
                name: active_item.name,
                type: active_item?.type === "task" ? "item" : active_item?.type,
                avatar_url: workspace_avatar ?? null,
            });
            this.is_back_clicked = false;
        },
        setHistorySelectionIndexOnClickBack() {
            const history_length = this.selection_histories.length;
            const unlinkedPanelState = useUnlinkedPanelState();
            if (unlinkedPanelState.isUnlinkedPanelActive) {
                this.selection_history_index += 1;
            }
            if (this.selection_history_index >= history_length) {
                this.selection_history_index = 0;
            }
            return this.selection_history_index;
        },
        loadCountries() {
            return axios.get(`/countries`).then((res) => {
                this.countries = res.data.data.lists;
            });
        },
        loadServices() {
            return axios.get(`/services`).then((res) => {
                //user_data.value = res.data
                this.services = res.data.data.lists;
            });
        },
        forceSignUpProcessIfNotCompleted(is_sign_up_complete = null) {
            this.show_control_panel = !Boolean(is_sign_up_complete);
            this.setControlAsSelectedModule(!Boolean(is_sign_up_complete));
            if (is_sign_up_complete) {
                /* Utilities().toggleChatPopoverWithoutUpdatingState(
                    this.is_conversation_open,
                );*/
            } else {
                this.show_control_panel_active_index = 1;
            }
        },

        setIsAgent(value) {
            this.is_agent = value;
        },

        setShowSupportAdminDialog(value = true, actionType = null) {
            this.show_support_admin_dialog = value;
            this.esa_action_type = actionType;
        },

        skipUpdateState() {
            this.skip_update_state = true;
        },

        continueUpdateState() {
            this.skip_update_state = false;
        },
        setIsdetailMode() {
            this.is_detail_mode = !this.is_detail_mode;
            this.is_dashboard_mode = false;
            const data = {
                item_view_mode: this.is_detail_mode ? "detail" : "normal",
            };
            axios.patch("/user-states", data);
        },
        toggleDashBoardOrNormalMode() {
            this.is_dashboard_mode = !this.is_dashboard_mode;
        },
        async fetchTimezonesBasedOnCountry(countryId = null) {
            if (!countryId) {
                return [];
            }
            try {
                const url = `timezones?country_id=${countryId}`;
                const response = await axios.get(url);

                const data = response.data?.data || [];
                const timezonesData = data.map((timezone) => {
                    return {
                        label: `UTC${timezone.offset} - ${timezone.abbreviation} - ${timezone.timezone}, ${timezone.name}`,
                        value: timezone.timezone,
                        id: timezone.id,
                    };
                });
                this.setOrganizationTimezones(timezonesData);
                return timezonesData;
            } catch (error) {
                console.error("Failed to fetch timezones:", error);
            }
        },
        setOrganizationTimezones(timezones) {
            this.organization_timezones = timezones;
        },
        async getColourTheme() {
            try {
                const res = await axios.get("color-theme");
                const data = res.data?.data;
                if (data) {
                    setColorsToStorage(data);
                    return data;
                }
            } catch (err) {}
        },
        async saveColourTheme(payload) {
            try {
                await axios.post("color-theme", payload);
            } catch (err) {
                Utilities().showErrorSnackbar(
                    err?.response?.data?.message ??
                        "Something went wrong. Please try again later.",
                );
            }
        },
        async getInvitationAccepted() {
            try {
                const res = await axios.get(`/invitations/accepted`);
                if (res?.data?.data?.[0]) {
                    this.invitation_accepted = res?.data?.data?.[0];
                } else {
                    this.invitation_accepted = null;
                }
            } catch (error) {
                console.error("Error occurred:", error);
            }
        },
        disableRestoreConversation() {
            this.restrictRestoreConversation = true;
        },
        enableRestoreConversation() {
            this.restrictRestoreConversation = false;
        },
    },
});
